// v10/src/components/admin/fuel/TankFormModal.js
//
// Modal de creación/edición de tanques de combustible (A9 — Tarea 2).
// Modo crear: cuando NO recibe `tank` prop. POST /api/fuel-tanks.
// Modo editar: cuando recibe `tank` prop. PUT /api/fuel-tanks/:id. fuel_type readonly.

import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, Plus, Save, Fuel } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import fuelService from '../../../services/fuelService';

const FUEL_TYPES = ['100LL', 'JET A-1', 'MOGAS', 'AVGAS'];

const TankFormModal = ({ tank, onSuccess, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const isEdit = Boolean(tank?.id);

  const [form, setForm] = useState(() => ({
    name:                 tank?.name || '',
    fuel_type:            tank?.fuel_type || FUEL_TYPES[0],
    capacity_liters:      tank?.capacity_liters != null ? String(tank.capacity_liters) : '',
    minimum_stock_liters: tank?.minimum_stock_liters != null ? String(tank.minimum_stock_liters) : '',
    notes:                tank?.notes || '',
  }));
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // ESC para cerrar
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Validación cliente
  const validation = useMemo(() => {
    const cap = Number(form.capacity_liters);
    const min = Number(form.minimum_stock_liters);
    const errors = [];
    const warnings = [];

    if (!form.name.trim())             errors.push(t('fuel_tank_form_err_name_required'));
    if (form.name.trim().length > 100) errors.push(t('fuel_tank_form_err_name_too_long'));
    if (!Number.isFinite(cap) || cap <= 0) errors.push(t('fuel_tank_form_err_capacity_positive'));
    if (!Number.isFinite(min) || min < 0)  errors.push(t('fuel_tank_form_err_minimum_nonneg'));
    if (Number.isFinite(cap) && Number.isFinite(min) && min > cap) {
      errors.push(t('fuel_tank_form_err_minimum_gt_capacity'));
    }

    // Warning en edit: si nueva capacidad < stock actual
    if (isEdit && Number.isFinite(cap)) {
      const currentStock = Number(tank?.current_stock_liters) || 0;
      if (cap < currentStock) {
        warnings.push(t('fuel_tank_form_warn_capacity_below_stock', { stock: currentStock }));
      }
    }
    return { errors, warnings, ok: errors.length === 0 };
  }, [form, isEdit, tank, t]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validation.ok || loading) return;

    setLoading(true); setError('');
    try {
      const payload = {
        name:                 form.name.trim(),
        capacity_liters:      Number(form.capacity_liters),
        minimum_stock_liters: Number(form.minimum_stock_liters),
        notes:                form.notes.trim() || null,
      };
      if (!isEdit) payload.fuel_type = form.fuel_type;

      if (isEdit) await fuelService.updateTank(tank.id, payload);
      else        await fuelService.createTank(payload);

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || t('fuel_validation_network_error'));
      setLoading(false);
    }
  };

  // Estilos compartidos
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const labelStyle = {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: fgFaint,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };
  const inputDisabled = {
    ...inputStyle,
    opacity: 0.7, cursor: 'not-allowed',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={loading ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 560, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 6px 16px rgba(79,70,229,0.32)',
              }}
            >
              <Fuel style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {isEdit ? t('fuel_tank_form_title_edit') : t('fuel_tank_form_title_new')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
                {isEdit ? t('fuel_tank_form_subtitle_edit') : t('fuel_tank_form_subtitle_new')}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={loading}
            aria-label="close"
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: loading ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Nombre */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('fuel_tank_form_label_name')}</p>
            <input
              type="text" maxLength={100} style={inputStyle}
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder={t('fuel_tank_form_placeholder_name')}
              autoFocus
            />
          </div>

          {/* Tipo de combustible */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('fuel_tank_form_label_fuel_type')}</p>
            {isEdit ? (
              <>
                <input type="text" readOnly value={form.fuel_type} style={inputDisabled} />
                <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint, lineHeight: 1.4 }}>
                  {t('fuel_tank_form_hint_fuel_type_locked')}
                </p>
              </>
            ) : (
              <select
                value={form.fuel_type}
                onChange={(e) => setField('fuel_type', e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
              >
                {FUEL_TYPES.map((ft) => <option key={ft} value={ft}>{ft}</option>)}
              </select>
            )}
          </div>

          {/* Capacidad y mínimo */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <p style={labelStyle}>{t('fuel_tank_form_label_capacity')}</p>
              <input
                type="number" min="0" step="0.01" style={inputStyle}
                value={form.capacity_liters}
                onChange={(e) => setField('capacity_liters', e.target.value)}
                placeholder="2000"
              />
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <p style={labelStyle}>{t('fuel_tank_form_label_minimum')}</p>
              <input
                type="number" min="0" step="0.01" style={inputStyle}
                value={form.minimum_stock_liters}
                onChange={(e) => setField('minimum_stock_liters', e.target.value)}
                placeholder="400"
              />
            </div>
          </div>

          {/* Notas */}
          <div style={{ marginBottom: 16 }}>
            <p style={labelStyle}>{t('fuel_tank_form_label_notes')}</p>
            <textarea
              rows={3} style={{ ...inputStyle, resize: 'vertical', minHeight: 70 }}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder={t('fuel_tank_form_placeholder_notes')}
            />
          </div>

          {/* Errores cliente */}
          {validation.errors.length > 0 && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              {validation.errors.map((e, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                  • {e}
                </p>
              ))}
            </div>
          )}

          {/* Warnings cliente */}
          {validation.warnings.length > 0 && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(234,88,12,0.08)',
                border: '1px solid rgba(234,88,12,0.25)',
                marginBottom: 12,
              }}
            >
              {validation.warnings.map((w, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: 12, color: '#ea580c', fontWeight: 600 }}>
                  ⚠ {w}
                </p>
              ))}
            </div>
          )}

          {/* Error backend */}
          {error && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
            </div>
          )}

          {/* Botones */}
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <motion.button
              type="submit" disabled={loading || !validation.ok}
              whileHover={!loading && validation.ok ? { scale: 1.02 } : {}}
              whileTap={!loading && validation.ok ? { scale: 0.97 } : {}}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none',
                cursor: loading || !validation.ok ? 'not-allowed' : 'pointer',
                background: loading || !validation.ok
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#4f46e5,#6366f1)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {loading
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : (isEdit ? <Save style={{ width: 16, height: 16 }} /> : <Plus style={{ width: 16, height: 16 }} />)
              }
              {loading
                ? t('fuel_tank_form_btn_saving')
                : (isEdit ? t('fuel_tank_form_btn_save') : t('fuel_tank_form_btn_create'))
              }
            </motion.button>
            <button
              type="button" onClick={onClose} disabled={loading}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted,
                fontFamily: 'inherit',
              }}
            >
              {t('fuel_modal_btn_cancel')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default TankFormModal;
